import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import { Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { usePermissions } from '@/shared/permissions';
import { useTenant } from '@/shared/tenant/useTenant';
import { EmptyState, SectionScaffold } from '@/shared/ui';

/**
 * Products section — the catalog workspace for the active tenant.
 *
 * The product list (search + table) will render here once the catalog data API
 * is wired; today it surfaces the configuration entry point so merchants can set
 * up catalog behaviour. Configuration lives behind the header gear.
 */
export function ProductsPage() {
  const { t } = useTranslation('app');
  const { hasPermission } = usePermissions();
  const { tenantName } = useTenant();
  const [search, setSearch] = useState('');

  const canView = hasPermission('settings.catalog.view');
  const tenantLabel = useMemo(() => tenantName ?? t('products.fallbackTenant'), [tenantName, t]);

  return (
    <SectionScaffold
      title={t('products.title')}
      subtitle={t('products.subtitle')}
      canView={canView}
      settingsTo="/settings/catalog"
      settingsLabel={t('products.settingsLabel')}
      search={{
        value: search,
        onChange: setSearch,
        placeholder: t('products.searchPlaceholder'),
        disabled: true,
        disabledHint: t('products.searchDisabledHint'),
      }}
      primaryAction={
        <Button variant="contained" startIcon={<AddRoundedIcon />} disabled>
          {t('products.addButton')}
        </Button>
      }
    >
      <EmptyState
        icon={<Inventory2RoundedIcon sx={{ fontSize: 48 }} />}
        title={t('products.empty.title')}
        description={t('products.empty.description', { tenant: tenantLabel })}
        action={
          <Button
            variant="outlined"
            component={RouterLink}
            to="/settings/catalog"
            startIcon={<SettingsRoundedIcon />}
          >
            {t('products.empty.action')}
          </Button>
        }
      />
    </SectionScaffold>
  );
}
